"use client";

import React, { useState } from "react";
import { Upload, Send, BookOpen, Heart, Sparkles, PenTool } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const StoryForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 text-center space-y-6 bg-accent/10 rounded-3xl border border-accent/20 backdrop-blur-sm animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-primary/20 flex items-center justify-center text-primary mb-4">
          <Sparkles className="size-10" />
        </div>
        <h3 className="text-3xl font-serif font-medium text-foreground">Story Received</h3>
        <p className="text-muted-foreground max-w-md text-lg">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-full px-8 border-primary/30 hover:bg-primary/10">
          Share Another Story
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="bg-accent/20 border-accent/20 focus-visible:ring-primary/50 placeholder:text-muted-foreground/60" />
              </FormControl>
              <FormDescription className="text-xs">Please provide the name you'd like us to use.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="bg-accent/20 border-accent/20 focus-visible:ring-primary/50 placeholder:text-muted-foreground/60" />
              </FormControl>
              <FormDescription className="text-xs">We'll use this to confirm receipt of your story.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="age" rules={{
        min: {
          value: 0,
          message: "Age must be positive"
        },
        max: {
          value: 120,
          message: "Please enter a valid age"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Your Age (optional)</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 12" {...field} className="bg-accent/20 border-accent/20 focus-visible:ring-primary/50 placeholder:text-muted-foreground/60" />
              </FormControl>
              <FormDescription className="text-xs">Sharing your age helps us understand the audience better.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="story_category" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Story Category</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-accent/20 border-accent/20 focus:ring-primary/50 w-full text-foreground placeholder:text-muted-foreground/60">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Personal Experience">Personal Experience</SelectItem>
                  <SelectItem value="Family Story">Family Story</SelectItem>
                  <SelectItem value="Therapeutic Journey">Therapeutic Journey</SelectItem>
                  <SelectItem value="Creative Writing">Creative Writing</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="coaching_interest" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Coaching Interest</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-accent/20 border-accent/20 focus:ring-primary/50 w-full text-foreground placeholder:text-muted-foreground/60">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="I would like to receive coaching">I would like to receive coaching</SelectItem>
                <SelectItem value="I am not interested in coaching">I am not interested in coaching</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-xs">Select if you would like to be contacted for coaching services.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="story_title" rules={{
      required: "Story title is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Story Title</FormLabel>
            <FormControl>
              <Input placeholder="A brief, descriptive title" {...field} className="bg-accent/20 border-accent/20 focus-visible:ring-primary/50 placeholder:text-muted-foreground/60 font-serif text-lg" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="story_content" rules={{
      required: "Please share your story"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Your Story</FormLabel>
            <FormControl>
              <Textarea placeholder="Write your story here..." {...field} className="min-h-[200px] bg-accent/20 border-accent/20 focus-visible:ring-primary/50 placeholder:text-muted-foreground/60 resize-y" />
            </FormControl>
            <FormDescription className="text-xs">Feel free to share as much detail as you're comfortable with.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="attachment" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Optional Media Attachment</FormLabel>
            <FormControl>
              <div className="flex items-center gap-4 p-4 rounded-xl border border-dashed border-accent/30 bg-accent/10 hover:bg-accent/20 transition-colors">
                <div className="p-2 rounded-full bg-primary/10 text-primary">
                  <Upload className="size-5" />
                </div>
                <Input type="file" accept="image/*" onChange={e => onChange(e.target.files?.[0])} {...field} className="bg-transparent border-0 p-0 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20" />
              </div>
            </FormControl>
            <FormDescription className="text-xs">JPG, PNG, or GIF (Max 5MB)</FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="p-4 rounded-xl bg-accent/10 border border-accent/20 text-sm text-muted-foreground">
        <p>
          By sharing your story, you agree to have it published on TheStoryHaven.org where it may inspire and support others in our community.
        </p>
      </div>

      {}
      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto px-8 py-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base shadow-lg shadow-primary/20 transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 disabled:opacity-50">
          {isSubmitting ? <>
              <span className="animate-spin mr-2"></span>
              Submitting...
            </> : <>
              <Send className="size-5 mr-2" />
              Submit Story
            </>}
        </Button>
      </div>

      {submitError && <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}
    </Form>;
};
export default function StorySubmissionForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section className="relative py-24 md:py-32 bg-gradient-to-b from-background via-accent/5 to-background overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-accent/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {}
          <div ref={ref} className={cn("lg:col-span-7 space-y-8 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/20 border border-accent/30 text-primary text-sm font-medium">
                <PenTool className="size-4" />
                <span>Share Your Voice</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground">
                Story Submission Form
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
                Your experiences matter. Whether it's a moment of triumph, a journey of healing, or a memory you hold dear, sharing your story can light the way for others. For direct inquiries, you can also reach us at <Link to="mailto:info@thestoryhaven.org" className="text-[#0071E3] hover:underline font-medium">info@thestoryhaven.org</Link>.
              </p>
            </div>

            <div className="bg-card/30 backdrop-blur-md border border-white/5 rounded-[2rem] p-6 md:p-8 shadow-2xl shadow-black/20">
              <FormProvider formId="story_submission_form_share_your_story" sectionName="share-your-story" defaultValues={{
              name: "",
              email: "",
              age: "",
              story_title: "",
              story_category: "",
              coaching_interest: "",
              story_content: "",
              attachment: undefined
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              story_title: {
                required: true
              },
              story_content: {
                required: true
              }
            }} submitText="Submit Story" successMessage="Thank you for sharing your story with us. Your voice is a gift to our community.">
                <StoryForm />
              </FormProvider>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 hidden lg:block sticky top-24">
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl shadow-black/40 border border-white/10 group">
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent z-10" />
              
              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-primary/10 blur-3xl group-hover:bg-primary/20 transition-all duration-700" />

              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570824/img/open_journal_gold_light.png" alt="An open journal bathed in soft golden light, inviting reflection and creativity" className="w-full h-auto object-cover aspect-[4/5] transform group-hover:scale-105 transition-transform duration-700 ease-out" />

              <div className="absolute bottom-0 left-0 w-full p-8 z-20">
                <div className="bg-background/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/20">
                      <BookOpen className="size-6" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-foreground mb-1">Safe & Secure</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Your story is handled with the utmost care and respect. We review every submission to ensure a safe environment for all.
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3 text-sm text-muted-foreground">
                    <Heart className="size-4 text-red-400 fill-red-400/20" />
                    <span>Join 500+ others who have shared</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}